import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Filter, X } from "./icons";
import AnimatedArrow from "./AnimatedArrow";
import AnimatedSearchInput from "./AnimatedSearchInput";
import useCatalogFilters from "./catalog/useCatalogFilters";
import { filtersToSearchParams } from "./catalog/filterUtils";
import CatalogSidebar from "./catalog/CatalogSidebar";
import CatalogToolbar from "./catalog/CatalogToolbar";
import type { Product } from "../types";
import { buildProductGroups } from "../lib/productGroups";
import ProductGrid from "./ProductGrid";
import ProductGridSkeleton from "./ProductGridSkeleton";
import { useLanguage } from "../context/LanguageContext";
import useGridLastRowBottom from "../hooks/useGridLastRowBottom";
import useMediaQuery from "../hooks/useMediaQuery";

const PREVIEW_ROWS = 2;

export default function ProductMatrix({ className = "" }: { className?: string }) {
  const { t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // Ghost text (inline autocomplete) + chip row, same as CatalogPage's
  // search bar — this preview submits by navigating to /catalog?q=..., but
  // it's the same search bar visually and should behave identically while
  // typing rather than feeling like a stripped-down version.
  const [suggestions, setSuggestions] = useState<{ ghost: string; chips: string[] }>({
    ghost: "",
    chips: [],
  });
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 3) {
      setSuggestions({ ghost: "", chips: [] });
      return;
    }
    const timeoutId = setTimeout(() => {
      fetch(`/api/search/suggestions?q=${encodeURIComponent(trimmed)}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data: { ghost: string; chips: string[] } | null) => {
          if (data) setSuggestions(data);
        })
        .catch(() => {
          // Suggestions are a nice-to-have on top of search, not required
          // for it to work — a failed fetch just leaves the row/ghost text
          // empty rather than surfacing an error to the visitor.
        });
    }, 180);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const navigate = useNavigate();
  const filters = useCatalogFilters(products);

  // Same drawer mechanics as CatalogPage/RFQDrawer — Escape closes it, and
  // body scroll is locked while it's open.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFilterPanelOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isFilterPanelOpen ? "hidden" : "";
  }, [isFilterPanelOpen]);

  // Matches the "2-3-4" breakpoints ProductGrid renders this preview with
  // (w-1/2 sm:w-1/3 xl:w-1/4) — the preview count has to track the same
  // breakpoints, or a fixed count that isn't a multiple of the current
  // column count strands a short, gappy row (e.g. 8 items in a 3-column
  // grid leaves 2 stranded in a third row). Rounding the preview up to a
  // full PREVIEW_ROWS worth of the *current* column count instead means a
  // short trailing row only happens when the filtered catalog genuinely
  // doesn't have enough matching products left to fill it — never because
  // of an arbitrary fixed cap.
  const isSm = useMediaQuery("(min-width: 640px)");
  const isXl = useMediaQuery("(min-width: 1280px)");
  const columnCount = isXl ? 4 : isSm ? 3 : 2;
  const previewGroups = buildProductGroups(filters.displayed || []).slice(
    0,
    columnCount * PREVIEW_ROWS,
  );

  // Same brand-picker/product-grid alignment as CatalogPage (see
  // useGridLastRowBottom) — the sidebar picker here targets the bottom of
  // this preview grid's last row.
  const gridWrapRef = useRef<HTMLDivElement>(null);
  const brandAlignY = useGridLastRowBottom(gridWrapRef, [previewGroups?.length]);

  // Carries whatever filters the user already picked in this preview into
  // the full catalog page, rather than dropping them at the door.
  const catalogParams = filtersToSearchParams({ ...filters, sort: filters.sortBy }).toString();
  const catalogHref = catalogParams ? `/catalog?${catalogParams}` : "/catalog";

  useEffect(() => {
    fetch("/api/products")
      .then((res) => res.json())
      .then(setProducts)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    navigate(`/catalog?q=${encodeURIComponent(trimmed)}`);
  };

  // A chip is a complete query on its own — clicking one both fills the
  // input and navigates immediately, matching CatalogPage's chip behavior
  // (the visitor shouldn't have to hit Enter a second time).
  const handleChipClick = (chip: string) => {
    setSearchQuery(chip);
    navigate(`/catalog?q=${encodeURIComponent(chip)}`);
  };

  return (
    <section id="equipment" className={`bg-steel-50 pt-8 pb-24 md:pt-12 md:pb-32 ${className}`}>
      <div className="container-industrial">
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-1.5">
          <AnimatedSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            className="flex-1"
            ghostSuggestion={suggestions.ghost}
            onAcceptGhost={setSearchQuery}
          />
          <button
            type="button"
            onClick={() => setIsFilterPanelOpen((v) => !v)}
            aria-label={t("filtersButton")}
            className="relative flex h-14 w-14 shrink-0 items-center justify-center border border-steel-200 bg-white text-steel-600 hover:border-accent hover:text-ink sm:w-auto sm:gap-2 sm:px-5 lg:hidden"
          >
            <Filter size={22} />
            <span className="hidden text-xs font-semibold uppercase tracking-wide2 sm:inline">
              {t("filtersButton")}
            </span>
            {filters.activeFilterCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center bg-accent px-1 text-[11px] font-semibold leading-none text-white">
                {filters.activeFilterCount}
              </span>
            )}
          </button>
        </form>

        {/* Fixed-height reserved slot, present whether or not suggestions are
            showing, so the grid below never shifts as chips appear/disappear —
            same treatment as CatalogPage's search bar. Sized tight to the
            chip buttons' own content height rather than padded out. */}
        <div className="mt-2 h-9">
          {/* Always mounted, animated via class toggle rather than
              mount/unmount — see CatalogPage's identical treatment. */}
          <div
            className={`no-scrollbar -mx-1 flex h-full flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap px-1 transition-all duration-200 ease-engineered ${
              suggestions.chips.length > 0
                ? "translate-y-0 opacity-100"
                : "pointer-events-none -translate-y-1 opacity-0"
            }`}
          >
            {suggestions.chips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => handleChipClick(chip)}
                className="shrink-0 whitespace-nowrap border border-steel-200 bg-white px-3 py-1.5 text-sm text-ink transition-colors duration-200 ease-engineered hover:border-accent hover:text-accent active:bg-steel-50"
              >
                <span className="font-semibold">{chip.slice(0, searchQuery.length)}</span>
                <span className="text-steel-500">{chip.slice(searchQuery.length)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-2 grid grid-cols-1 gap-10 lg:grid-cols-[240px_1fr]">
          {/* hidden (not just the aside inside it) so this whole grid item is
              removed from layout on mobile — a merely-empty-but-still-present
              grid item still claims a full gap-10 row gap even at zero
              height, which was stacking with the fixed-height suggestion
              slot above into a lot of dead space under the search bar. */}
          <div className="hidden lg:flex lg:flex-col lg:gap-6">
            <aside>
              <CatalogSidebar filters={filters} brandPickerAlignBottomY={brandAlignY} />
            </aside>
          </div>

          {/* Mobile filter drawer — same slide-out mechanics as CatalogPage. */}
          <div
            onClick={() => setIsFilterPanelOpen(false)}
            className={`fixed inset-0 z-[60] bg-ink/60 transition-opacity duration-300 ease-engineered lg:hidden ${
              isFilterPanelOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
            }`}
          />
          <aside
            aria-hidden={!isFilterPanelOpen}
            className={`fixed inset-y-0 left-0 z-[70] flex w-full max-w-xs flex-col border-r border-steel-200 bg-white transition-transform duration-300 ease-engineered lg:hidden ${
              isFilterPanelOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between border-b border-steel-200 px-5 py-4">
              <h2 className="font-sans text-sm font-semibold text-ink">{t("filtersButton")}</h2>
              <button
                type="button"
                aria-label={t("catalogCloseFiltersAria")}
                onClick={() => setIsFilterPanelOpen(false)}
                className="flex h-9 w-9 items-center justify-center text-ink transition-colors duration-150 ease-engineered hover:bg-steel-900/5 hover:text-accent active:bg-steel-900/5"
              >
                <X size={20} />
              </button>
            </div>
            <div className="no-scrollbar flex-1 overflow-y-auto p-5">
              <CatalogSidebar filters={filters} />
            </div>
          </aside>

          <div>
            <CatalogToolbar filters={filters} />
            {isLoading ? (
              <ProductGridSkeleton count={columnCount * PREVIEW_ROWS} />
            ) : (
              <>
                <ProductGrid gridRef={gridWrapRef} groups={previewGroups || []} columns="2-3-4" />
                {(filters.displayed || []).length === 0 && (
                  <p className="mt-6 text-sm text-steel-500">{t("catalogEmptyFilters")}</p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-14 flex justify-center">
          <Link to={catalogHref} className="btn-dark-outline">
            {t("productMatrixFullCatalog")}
            <AnimatedArrow />
          </Link>
        </div>
      </div>
    </section>
  );
}
